/**
 * Takeoff mode UI shared by the editors: catalog rail (items, host assemblies,
 * areas, typical units, sheet scale + masks), totals panel, export menu and the
 * calibration form. Canvas tools live in each editor (web/excalidraw first).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { scopeChoices, type ScopeChoices } from '../scopes';
import { ScopeSelect } from './ScopeSelect';
import type { TakeoffSession, TakeoffTarget } from '../hooks/useTakeoff';
import {
  CATEGORIES,
  MASK_KINDS,
  SYMBOLS,
  TAKEOFF_COLORS,
  TYPE_PREFIXES,
  UOMS,
  fmtQty,
  nextTypeCode,
  parseLength,
  symbolPath,
  type ComponentDTO,
  type TakeoffAssemblyDTO,
  type TakeoffItemDTO,
  type TakeoffSymbol,
} from '../takeoff';
import { Icon, Modal, toast } from './basics';

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

export function SymbolSwatch({ symbol, color, size = 14, uom }: { symbol: TakeoffSymbol; color: string; size?: number; uom?: string }) {
  const r = size / 2 - 1;
  if (uom === 'LF' || uom === 'M') {
    // Runs are drawn as lines, so their swatch is a line.
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="rl-to-swatch" data-kind="run">
        <path d={`M1 ${size - 3} Q ${size / 2} 1 ${size - 1} ${size / 2}`} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" />
      </svg>
    );
  }
  if (uom === 'SF' || uom === 'M2') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="rl-to-swatch" data-kind="area">
        <rect x={1.5} y={1.5} width={size - 3} height={size - 3} fill={color} fillOpacity={0.35} stroke={color} strokeWidth={1.5} strokeDasharray="3 2" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="rl-to-swatch">
      <path d={symbolPath(symbol, size / 2, size / 2, r)} fill={color} stroke="rgba(0,0,0,.35)" strokeWidth={0.75} />
    </svg>
  );
}

function CheckBadge({ check, actual, expected }: { check: 'ok' | 'mismatch' | 'none'; actual: number; expected: number | null }) {
  if (check === 'none') return null;
  return check === 'ok' ? (
    <span className="rl-to-check" data-state="ok" title={`Matches the expected ${fmtQty(expected)}`}>
      ✓
    </span>
  ) : (
    <span className="rl-to-check" data-state="mismatch" title={`Counted ${fmtQty(actual)}, expected ${fmtQty(expected)}`}>
      ⚠ {fmtQty(actual)}/{fmtQty(expected)}
    </span>
  );
}

/** Text/number input that commits on blur or Enter (live for collaborators). */
function Commit({ value, onCommit, type = 'text', placeholder, width, testId, title }: { value: string | number | null | undefined; onCommit(v: string): void; type?: string; placeholder?: string; width?: number; testId?: string; title?: string }) {
  const [v, setV] = useState(value == null ? '' : String(value));
  useEffect(() => setV(value == null ? '' : String(value)), [value]);
  const commit = () => {
    if (v !== (value == null ? '' : String(value))) onCommit(v);
  };
  return (
    <input
      className="rl-input"
      data-size="sm"
      type={type}
      value={v}
      placeholder={placeholder}
      title={title}
      style={width ? { width } : undefined}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setV(value == null ? '' : String(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      data-testid={testId}
    />
  );
}

function Section({ title, children, extra, open: initial = true, testId }: { title: string; children: ReactNode; extra?: ReactNode; open?: boolean; testId?: string }) {
  const [open, setOpen] = useState(initial);
  return (
    <section className="rl-to-section" data-testid={testId}>
      <div className="rl-to-section-h">
        <button className="rl-to-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? '▾' : '▸'} {title}
        </button>
        <span className="rl-spacer" />
        {extra}
      </div>
      {open ? <div className="rl-to-section-b">{children}</div> : null}
    </section>
  );
}

const sameTarget = (a: TakeoffTarget | null, kind: 'item' | 'assembly', id: string) => !!a && a.kind === kind && a.id === id;

// ---------------------------------------------------------------------------
// Catalog rail
// ---------------------------------------------------------------------------

export function TakeoffRail({ t, page }: { t: TakeoffSession; page: number }) {
  const [editItem, setEditItem] = useState<TakeoffItemDTO | 'new' | null>(null);
  const [editAssembly, setEditAssembly] = useState<TakeoffAssemblyDTO | 'new' | null>(null);
  const [filter, setFilter] = useState('');
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const q of t.quantities?.measurements ?? []) if (q.page_index === page && q.type === 'count') m.set(q.target_id, (m.get(q.target_id) ?? 0) + 1);
    return m;
  }, [t.quantities, page]);
  if (!t.state) return <div className="rl-to-rail rl-center">{t.error ? <p className="rl-error">{t.error}</p> : <span className="rl-spinner" />}</div>;
  const f = filter.trim().toLowerCase();
  const items = t.items.filter((i) => !f || `${i.code} ${i.description} ${i.name} ${i.make} ${i.model}`.toLowerCase().includes(f));
  const hosts = t.assemblies.filter((a) => !f || `${a.name} ${a.code}`.toLowerCase().includes(f));
  const pick = (target: TakeoffTarget, measure: 'count' | 'linear' | 'area' = 'count') => t.setTarget(target, measure);
  // A linked Estimating Workbook owns the catalog: every item then carries a workbook_state.
  const linked = t.items.some((i) => !!i.workbook_state);

  return (
    <div className="rl-to-rail" data-testid="takeoff-rail">
      <input className="rl-input" data-size="sm" placeholder="Search catalog…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <Section
        title={`Assemblies (${t.assemblies.length})`}
        testId="takeoff-hosts"
        extra={
          <button className="rl-icon-btn" title="New assembly" onClick={() => setEditAssembly('new')} data-testid="new-host">
            <Icon name="plus" size={14} />
          </button>
        }
      >
        {hosts.map((a) => (
          <HostRow key={a.id} t={t} a={a} active={sameTarget(t.ui.target, 'assembly', a.id)} onPick={() => pick({ kind: 'assembly', id: a.id }, a.measure)} onEdit={() => setEditAssembly(a)} pageCount={counts.get(a.id) ?? 0} />
        ))}
        {!hosts.length ? <p className="rl-muted rl-to-empty">An assembly (pylon, pole, typical room) owns the marks; its package multiplies into every item.</p> : null}
      </Section>
      <Section
        title={`Items (${t.items.length})`}
        testId="takeoff-items"
        extra={
          <button className="rl-icon-btn" title="New item (TYPE)" onClick={() => setEditItem('new')} data-testid="new-item">
            <Icon name="plus" size={14} />
          </button>
        }
      >
        {items.map((i) => {
          const active = sameTarget(t.ui.target, 'item', i.id);
          return (
            <div key={i.id} className="rl-to-row" data-active={active || undefined} data-testid={`item-${i.code}`}>
              <button className="rl-to-pick" onClick={() => pick({ kind: 'item', id: i.id }, i.uom === 'LF' || i.uom === 'M' ? 'linear' : i.uom === 'SF' || i.uom === 'M2' ? 'area' : 'count')} title="Count this item (Q / W / A)">
                <SymbolSwatch symbol={i.symbol} color={i.color} uom={i.uom} />
                <b>{i.code}</b>
                <span className="name">{i.description || i.name}</span>
                {i.verify || i.source === 'inferred' ? <span className="rl-to-flag" title="Inferred — verify">verify</span> : null}
                {i.favorite_slot ? <kbd>{i.favorite_slot}</kbd> : null}
                {i.workbook_state === 'removed' ? (
                  <span className="rl-est-wb" data-kind="warn" title="No longer in PROJECT BOM: its marks are kept but not counted">
                    removed from workbook
                  </span>
                ) : i.workbook_state === 'pending' ? (
                  <span className="rl-est-wb" title="A TYPE-only row is being added to PROJECT BOM">
                    adding…
                  </span>
                ) : null}
                <span className="rl-to-count" title="Marks on this sheet">{counts.get(i.id) ?? 0}</span>
              </button>
              <button className="rl-icon-btn" title="Edit item" aria-label={`Edit ${i.code}`} onClick={() => setEditItem(i)}>
                ✎
              </button>
            </div>
          );
        })}
        {!t.items.length ? <p className="rl-muted rl-to-empty">Add the fixture schedule's TYPEs (LD01, UP01 …), or count items directly on interior jobs.</p> : null}
      </Section>
      <AreasSection t={t} page={page} />
      <UnitsSection t={t} page={page} />
      <SheetSection t={t} page={page} />
      {editItem ? <ItemEditor t={t} item={editItem === 'new' ? null : editItem} linked={linked} onClose={() => setEditItem(null)} /> : null}
      {editAssembly ? <AssemblyEditor t={t} assembly={editAssembly === 'new' ? null : editAssembly} onClose={() => setEditAssembly(null)} /> : null}
    </div>
  );
}

function HostRow({ t, a, active, onPick, onEdit, pageCount }: { t: TakeoffSession; a: TakeoffAssemblyDTO; active: boolean; onPick(): void; onEdit(): void; pageCount: number }) {
  const [open, setOpen] = useState(active);
  const host = t.quantities?.hosts.find((h) => h.assembly_id === a.id);
  const setQty = (index: number, qty: number) => {
    const components = a.components.map((c, i) => ({ item: c.item, child_assembly: c.child_assembly, qty_per_host: i === index ? qty : c.qty_per_host, qty_label: c.qty_label, notes: c.notes ?? '' }));
    void t.update('assemblies', a, { components });
  };
  return (
    <div className="rl-to-host" data-active={active || undefined} data-testid={`host-${a.name}`}>
      <div className="rl-to-row">
        <button className="rl-to-toggle" onClick={() => setOpen(!open)} aria-label={open ? 'Collapse package' : 'Expand package'}>
          {open ? '▾' : '▸'}
        </button>
        <button className="rl-to-pick" onClick={onPick} title={`Count ${a.name} (Q)`}>
          <SymbolSwatch symbol={a.symbol} color={a.color} />
          <span className="name">{a.name}</span>
          {a.favorite_slot ? <kbd>{a.favorite_slot}</kbd> : null}
          <span className="rl-to-count" title="Marks on this sheet">{pageCount}</span>
        </button>
        {host ? <CheckBadge check={host.check} actual={host.count} expected={host.expected_count} /> : null}
        <button className="rl-icon-btn" title="Edit host" aria-label={`Edit ${a.name}`} onClick={onEdit}>
          ✎
        </button>
      </div>
      {open ? (
        <div className="rl-to-package">
          {a.components.map((c, i) => {
            const item = c.item ? t.itemById.get(c.item) : null;
            const child = c.child_assembly ? t.assemblyById.get(c.child_assembly) : null;
            return (
              <div key={c.id ?? i} className="rl-to-comp">
                <span className="name">{item ? `${item.code} ${item.description || item.name}` : child ? `▸ ${child.name}` : '—'}</span>
                <label title={c.label ?? c.qty_label}>
                  <Commit type="number" value={c.qty_per_host} width={56} onCommit={(v) => setQty(i, Number(v) || 0)} testId={`qty-${a.name}-${item?.code ?? child?.name}`} />
                  <span className="rl-muted"> {c.label ?? `qty per ${a.host_word}`}</span>
                </label>
              </div>
            );
          })}
          {!a.components.length ? <p className="rl-muted rl-to-empty">No package yet — edit the assembly to add items.</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function AreasSection({ t, page }: { t: TakeoffSession; page: number }) {
  const areas = (t.state?.areas ?? []).filter((a) => a.page_index == null || a.page_index === page);
  return (
    <Section
      title={`Areas (${areas.length})`}
      open={false}
      testId="takeoff-areas"
      extra={
        <button className="rl-btn" data-size="sm" onClick={() => t.setTool('zone')} title="Draw an area outline on this sheet (default area = sheet number)">
          Draw
        </button>
      }
    >
      <label className="rl-to-inline">
        Default scope <Commit value={t.state?.default_scope} width={110} onCommit={(v) => void t.client.setDefaultScope(v).then(() => t.reload())} />
      </label>
      {areas.map((a) => (
        <div key={a.id} className="rl-to-row">
          <span className="rl-to-dot" style={{ background: a.color }} />
          <Commit value={a.name} onCommit={(v) => void t.update('areas', a, { name: v })} />
          <Commit value={a.scope} placeholder="scope" width={90} onCommit={(v) => void t.update('areas', a, { scope: v.toUpperCase() })} />
          <button className="rl-icon-btn" aria-label={`Delete ${a.name}`} onClick={() => void t.remove('areas', a.id)}>
            <Icon name="close" size={12} />
          </button>
        </div>
      ))}
    </Section>
  );
}

function UnitsSection({ t, page }: { t: TakeoffSession; page: number }) {
  const units = t.state?.unit_types ?? [];
  return (
    <Section
      title={`Typical units (${units.length})`}
      open={false}
      testId="takeoff-units"
      extra={
        <button className="rl-icon-btn" title="New typical unit" onClick={() => void t.create('unit_types', { name: `Unit type ${units.length + 1}`, multiplier: 1 })} data-testid="new-unit">
          <Icon name="plus" size={14} />
        </button>
      }
    >
      {units.map((u) => (
        <div key={u.id} className="rl-to-row" data-testid={`unit-${u.name}`}>
          <span className="rl-to-dot" style={{ background: u.color }} />
          <Commit value={u.name} onCommit={(v) => void t.update('unit_types', u, { name: v })} />
          <span className="rl-muted">×</span>
          <Commit type="number" value={u.multiplier} width={56} onCommit={(v) => void t.update('unit_types', u, { multiplier: Math.max(1, Number(v) || 1) })} testId={`unit-mult-${u.name}`} />
          <button className="rl-btn" data-size="sm" title="Draw this unit's region on the sheet" onClick={() => t.uiStore.set({ tool: 'unit', unitTypeId: u.id })}>
            Region{u.regions.filter((r) => r.page_index === page).length ? ` (${u.regions.filter((r) => r.page_index === page).length})` : ''}
          </button>
          <button className="rl-icon-btn" aria-label={`Delete ${u.name}`} onClick={() => void t.remove('unit_types', u.id)}>
            <Icon name="close" size={12} />
          </button>
        </div>
      ))}
    </Section>
  );
}

function SheetSection({ t, page }: { t: TakeoffSession; page: number }) {
  const sheet = t.sheetFor(page);
  const masks = (t.state?.masks ?? []).filter((m) => m.page_index === page);
  const presets = t.state?.scale_presets ?? [];
  return (
    <Section title={`Sheet ${sheet?.label ?? `P${page + 1}`}`} testId="takeoff-sheet">
      <div className="rl-to-grid">
        <label>
          Sheet no.
          <Commit value={sheet?.sheet_number} placeholder={`P${page + 1}`} onCommit={(v) => void t.setSheet(page, { sheet_number: v })} testId="sheet-number" />
        </label>
        <label>
          Title
          <Commit value={sheet?.title} onCommit={(v) => void t.setSheet(page, { title: v })} />
        </label>
      </div>
      <div className="rl-to-row">
        <span className="rl-muted">Scale</span>
        <select
          className="rl-select"
          data-size="sm"
          value={sheet?.scale_method === 'preset' ? sheet.scale_label : ''}
          onChange={(e) => e.target.value && void t.setSheet(page, { scale: { preset: e.target.value } })}
          data-testid="scale-preset"
        >
          <option value="">{sheet?.points_per_unit ? (sheet.scale_method === 'calibrated' ? sheet.scale_label : 'custom') : 'Not set'}</option>
          {presets.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </select>
        <button className="rl-btn" data-size="sm" onClick={() => t.setTool('calibrate')} title="Draw over a known dimension (Shift+C)" data-testid="calibrate">
          Calibrate
        </button>
      </div>
      <div className="rl-to-row">
        <span className="rl-muted">Masks</span>
        <span className="rl-spacer" />
        <button className="rl-btn" data-size="sm" onClick={() => t.setTool('mask')} title="Outline the legend / title block / notes (left out of text search and auto-count)" data-testid="draw-mask">
          Draw mask
        </button>
      </div>
      {masks.map((m) => (
        <div key={m.id} className="rl-to-row">
          <select className="rl-select" data-size="sm" value={m.kind} onChange={(e) => void t.update('masks', m, { kind: e.target.value })}>
            {MASK_KINDS.map((k) => (
              <option key={k} value={k}>
                {k.replace('_', ' ')}
              </option>
            ))}
          </select>
          <button className="rl-btn" data-size="sm" title="Copy to every other sheet" onClick={() => void t.client.copyMask(m.id, 'all').then(() => t.reload())}>
            All sheets
          </button>
          <button className="rl-icon-btn" aria-label="Delete mask" onClick={() => void t.remove('masks', m.id)}>
            <Icon name="close" size={12} />
          </button>
        </div>
      ))}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Editors
// ---------------------------------------------------------------------------

type ItemDraft = Omit<TakeoffItemDTO, 'id' | 'version' | 'sort_order'>;

const blankItem = (code: string, color: string): ItemDraft => ({
  code,
  name: '',
  description: '',
  make: '',
  model: '',
  part_no: '',
  wattage: '',
  accessories: '',
  notes: '',
  uom: 'EACH',
  category: 'luminaire',
  color,
  symbol: 'circle',
  zone: '',
  spec_reference: '',
  source: 'schedule',
  verify: false,
  expected_total: null,
  waste_pct: 0,
  run_allowance: 0,
  unit_cost: null,
  favorite_slot: null,
});

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className="rl-to-field" data-wide={wide || undefined}>
      <span>{label}</span>
      {children}
    </label>
  );
}

/**
 * `linked`: an Estimating Workbook owns the catalog (TYPE, description, make, model,
 * pricing…); the editor only offers what the board owns: a new TYPE's code /
 * description / UOM (written once as a TYPE-only PROJECT BOM row), the board's
 * symbol and colour, the key slot and quantity rules.
 */
export function ItemEditor({ t, item, onClose, linked = false, scopes }: { t: TakeoffSession; item: TakeoffItemDTO | null; onClose(): void; linked?: boolean; scopes?: ScopeChoices }) {
  const fromWorkbook = linked && !!item;
  const [prefix, setPrefix] = useState('LD');
  const [d, setD] = useState<ItemDraft>(() => (item ? { ...item } : blankItem(nextTypeCode('LD', t.items), TAKEOFF_COLORS[t.items.length % TAKEOFF_COLORS.length])));
  const set = <K extends keyof ItemDraft>(k: K, v: ItemDraft[K]) => setD((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const data = { ...d, code: d.code.trim().toUpperCase(), description: d.description.toUpperCase() };
    const ok = item ? await t.update('items', item, data) : await t.create('items', data);
    if (ok) onClose();
  };
  return (
    <Modal
      title={item ? `Edit ${item.code}` : 'New item'}
      onClose={onClose}
      footer={
        <>
          {item ? (
            <>
              <button className="rl-btn" data-variant="danger" onClick={() => void t.remove('items', item.id).then(onClose)}>
                Delete
              </button>
              <button className="rl-btn" onClick={() => void t.client.duplicateItem(item.id).then(() => (t.reload(), onClose()))}>
                Duplicate
              </button>
            </>
          ) : null}
          <span className="rl-spacer" />
          <button className="rl-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="rl-btn" data-variant="primary" onClick={() => void save()} data-testid="item-save">
            Save
          </button>
        </>
      }
    >
      <div className="rl-to-form" data-testid="item-editor">
        <Field label="TYPE">
          <span className="rl-to-inline">
            {!item ? (
              <select
                className="rl-select"
                data-size="sm"
                value={prefix}
                onChange={(e) => {
                  setPrefix(e.target.value);
                  set('code', nextTypeCode(e.target.value, t.items));
                }}
                aria-label="Type prefix"
              >
                {TYPE_PREFIXES.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            ) : null}
            <input className="rl-input" value={d.code} readOnly={fromWorkbook} disabled={fromWorkbook} title={fromWorkbook ? 'From the linked workbook — rename the TYPE in PROJECT BOM' : undefined} onChange={(e) => set('code', e.target.value.toUpperCase())} data-testid="item-code" />
          </span>
        </Field>
        <Field label="Device description" wide>
          <input className="rl-input" value={d.description} readOnly={fromWorkbook} disabled={fromWorkbook} title={fromWorkbook ? 'From the linked workbook — edit the description in PROJECT BOM' : undefined} onChange={(e) => set('description', e.target.value)} placeholder="RECESSED DOWNLIGHT" data-testid="item-description" />
        </Field>
        {linked ? (
          <p className="rl-muted" style={{ gridColumn: '1 / -1', margin: 0, fontSize: 12 }} data-testid="item-workbook-note">
            {fromWorkbook
              ? '🔒 TYPE, description and unit come from the linked workbook, as do make, model, part no. and pricing — edit them in PROJECT BOM and the board follows within a few seconds. Symbol, colour and quantity rules are the board’s.'
              : 'A new TYPE is added to the workbook as a TYPE-only PROJECT BOM row; fill in make, model and pricing there.'}
          </p>
        ) : null}
        {!linked ? (
          <>
        <Field label="Make">
          <input className="rl-input" value={d.make} onChange={(e) => set('make', e.target.value)} />
        </Field>
        <Field label="Model">
          <input className="rl-input" value={d.model} onChange={(e) => set('model', e.target.value)} />
        </Field>
        <Field label="Part no.">
          <input className="rl-input" value={d.part_no} onChange={(e) => set('part_no', e.target.value)} />
        </Field>
        <Field label="Wattage">
          <input className="rl-input" value={d.wattage} onChange={(e) => set('wattage', e.target.value)} />
        </Field>
          </>
        ) : null}
        <Field label="Unit of measure">
          <select className="rl-select" value={d.uom} disabled={fromWorkbook} title={fromWorkbook ? 'From the linked workbook — change the unit in PROJECT BOM' : undefined} onChange={(e) => set('uom', e.target.value as ItemDraft['uom'])} data-testid="item-uom">
            {UOMS.map((u) => (
              <option key={u}>{u}</option>
            ))}
          </select>
        </Field>
        {!linked ? (
          <>
        <Field label="Category">
          <select className="rl-select" value={d.category} onChange={(e) => set('category', e.target.value as ItemDraft['category'])}>
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Unit cost">
          <input className="rl-input" type="number" min={0} step="0.01" value={d.unit_cost ?? ''} onChange={(e) => set('unit_cost', e.target.value === '' ? null : e.target.value)} placeholder="TBD" data-testid="item-unit-cost" />
        </Field>
        <Field label="Price source">
          <select className="rl-select" value={d.price_source ?? ''} onChange={(e) => set('price_source', e.target.value as ItemDraft['price_source'])} data-testid="item-price-source">
            <option value="">—</option>
            <option value="list">List price</option>
            <option value="quote">Quote</option>
            <option value="historical">Historical</option>
            <option value="guess">Guess</option>
          </select>
        </Field>
        <Field label="Confidence">
          <select className="rl-select" value={d.confidence ?? ''} onChange={(e) => set('confidence', e.target.value as ItemDraft['confidence'])}>
            <option value="">—</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Field>
        <Field label="Labour h / unit">
          <input className="rl-input" type="number" min={0} step="0.25" value={d.labour_hours ?? ''} onChange={(e) => set('labour_hours', e.target.value === '' ? null : Number(e.target.value))} placeholder="or %" data-testid="item-labour-hours" />
        </Field>
        <Field label="Labour % of material">
          <input className="rl-input" type="number" min={0} value={d.labour_pct ?? ''} onChange={(e) => set('labour_pct', e.target.value === '' ? null : Number(e.target.value))} placeholder="project default" />
        </Field>
        <Field label="Accessories" wide>
          <input className="rl-input" value={d.accessories} onChange={(e) => set('accessories', e.target.value)} />
        </Field>
        <Field label="Spec reference">
          <input className="rl-input" value={d.spec_reference} onChange={(e) => set('spec_reference', e.target.value)} placeholder="E-6 Detail 2" />
        </Field>
        <Field label="Source">
          <select className="rl-select" value={d.source} onChange={(e) => set('source', e.target.value as ItemDraft['source'])}>
            <option value="schedule">Schedule</option>
            <option value="inferred">Inferred (verify)</option>
            <option value="design">Eos design</option>
          </select>
        </Field>
          </>
        ) : null}
        <Field label="Expected total">
          <input className="rl-input" type="number" value={d.expected_total ?? ''} onChange={(e) => set('expected_total', e.target.value === '' ? null : Number(e.target.value))} data-testid="item-expected" />
        </Field>
        <Field label="Zone (flat counts)">
          <input className="rl-input" value={d.zone} onChange={(e) => set('zone', e.target.value.toUpperCase())} />
        </Field>
        <Field label="Scope (BASE BID / option)">
          <ScopeSelect value={d.scope ?? ''} onChange={(v) => set('scope', v)} choices={scopes ?? scopeChoices(null)} inheritLabel="frame / BASE BID" testId="item-scope" />
        </Field>
        <Field label="Allowance per run">
          <input className="rl-input" type="number" value={d.run_allowance} onChange={(e) => set('run_allowance', Number(e.target.value) || 0)} />
        </Field>
        <Field label="Waste %">
          <input className="rl-input" type="number" value={d.waste_pct} onChange={(e) => set('waste_pct', Number(e.target.value) || 0)} />
        </Field>
        {d.uom === 'EACH' || d.uom === 'SPOOL' || d.uom === 'LOT' ? (
          <Field label="Symbol">
            <SymbolPicker value={d.symbol} color={d.color} onChange={(s) => set('symbol', s)} />
          </Field>
        ) : (
          <Field label="Drawn as">
            <p className="rl-muted" style={{ margin: 0, fontSize: 12 }} data-testid="item-drawn-as">
              {d.uom === 'SF' || d.uom === 'M2' ? 'An area outline' : 'A line'} in this colour — {d.uom === 'SF' || d.uom === 'M2' ? 'areas' : 'runs'} have no symbol.
            </p>
          </Field>
        )}
        <Field label="Colour">
          <ColorRow value={d.color} onChange={(c) => set('color', c)} />
        </Field>
        <Field label="Key (1–9)">
          <input className="rl-input" type="number" min={1} max={9} value={d.favorite_slot ?? ''} onChange={(e) => set('favorite_slot', e.target.value === '' ? null : Math.max(1, Math.min(9, Number(e.target.value))))} />
        </Field>
        {!linked ? (
          <>
        <Field label="Verify flag">
          <input type="checkbox" checked={d.verify} onChange={(e) => set('verify', e.target.checked)} />
        </Field>
        <Field label="Notes" wide>
          <textarea className="rl-textarea" rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Qty basis, RFI flags …" />
        </Field>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function SymbolPicker({ value, color, onChange }: { value: TakeoffSymbol; color: string; onChange(s: TakeoffSymbol): void }) {
  return (
    <span className="rl-to-inline">
      {SYMBOLS.map((s) => (
        <button key={s} className="rl-to-sym" data-active={s === value || undefined} onClick={() => onChange(s)} title={s} type="button">
          <SymbolSwatch symbol={s} color={color} size={16} />
        </button>
      ))}
    </span>
  );
}

function ColorRow({ value, onChange }: { value: string; onChange(c: string): void }) {
  return (
    <span className="rl-to-inline">
      {TAKEOFF_COLORS.map((c) => (
        <button key={c} type="button" className="rl-to-color" data-active={c === value || undefined} style={{ background: c }} onClick={() => onChange(c)} aria-label={c} />
      ))}
    </span>
  );
}

type AssemblyDraft = Pick<TakeoffAssemblyDTO, 'name' | 'code' | 'host_label' | 'zone' | 'scope' | 'description' | 'notes' | 'color' | 'symbol' | 'measure' | 'expected_count' | 'spec_reference' | 'favorite_slot'> & {
  components: ComponentDTO[];
};

export function AssemblyEditor({ t, assembly, onClose, scopes }: { t: TakeoffSession; assembly: TakeoffAssemblyDTO | null; onClose(): void; scopes?: ScopeChoices }) {
  const [d, setD] = useState<AssemblyDraft>(() =>
    assembly
      ? { ...assembly, components: assembly.components.map((c) => ({ ...c })) }
      : { name: '', code: '', host_label: '', zone: '', scope: '', description: '', notes: '', color: TAKEOFF_COLORS[(t.assemblies.length + 1) % TAKEOFF_COLORS.length], symbol: 'diamond', measure: 'count', expected_count: null, spec_reference: '', favorite_slot: null, components: [] },
  );
  const set = <K extends keyof AssemblyDraft>(k: K, v: AssemblyDraft[K]) => setD((s) => ({ ...s, [k]: v }));
  const host = (d.host_label || d.name || 'host').toLowerCase();
  const save = async () => {
    if (!d.name.trim()) {
      toast('Give the assembly a name', 'warning');
      return;
    }
    const data = { ...d, components: d.components.filter((c) => c.item || c.child_assembly).map((c, i) => ({ item: c.item, child_assembly: c.child_assembly, qty_per_host: Number(c.qty_per_host) || 0, qty_label: c.qty_label, notes: c.notes ?? '', sort_order: i })) };
    const ok = assembly ? await t.update('assemblies', assembly, data) : await t.create('assemblies', data);
    if (ok) onClose();
  };
  return (
    <Modal
      title={assembly ? `Edit ${assembly.name}` : 'New assembly'}
      onClose={onClose}
      footer={
        <>
          {assembly ? (
            <button className="rl-btn" data-variant="danger" onClick={() => void t.remove('assemblies', assembly.id).then(onClose)}>
              Delete
            </button>
          ) : null}
          <span className="rl-spacer" />
          <button className="rl-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="rl-btn" data-variant="primary" onClick={() => void save()} data-testid="host-save">
            Save
          </button>
        </>
      }
    >
      <div className="rl-to-form" data-testid="host-editor">
        <Field label="Name" wide>
          <input className="rl-input" value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="Refurbished Pylon" data-testid="host-name" />
        </Field>
        <Field label="Counted as (for “qty per …”)">
          <input className="rl-input" value={d.host_label} onChange={(e) => set('host_label', e.target.value)} placeholder="pylon" data-testid="host-word" />
        </Field>
        <Field label="Zone (workbook)">
          <input className="rl-input" value={d.zone} onChange={(e) => set('zone', e.target.value.toUpperCase())} placeholder="PYLONS" />
        </Field>
        <Field label="Scope (BASE BID / option)">
          <ScopeSelect value={d.scope ?? ''} onChange={(v) => set('scope', v)} choices={scopes ?? scopeChoices(null)} inheritLabel="frame / BASE BID" testId="host-scope" />
        </Field>
        <Field label="Code">
          <input className="rl-input" value={d.code} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="RP" />
        </Field>
        <Field label="Expected count">
          <input className="rl-input" type="number" value={d.expected_count ?? ''} onChange={(e) => set('expected_count', e.target.value === '' ? null : Number(e.target.value))} data-testid="host-expected" />
        </Field>
        <Field label="Measured as">
          <select className="rl-select" value={d.measure} onChange={(e) => set('measure', e.target.value as AssemblyDraft['measure'])}>
            <option value="count">Count (one mark per host)</option>
            <option value="linear">Linear (package per foot)</option>
            <option value="area">Area (package per sq ft)</option>
          </select>
        </Field>
        <Field label="Spec reference">
          <input className="rl-input" value={d.spec_reference} onChange={(e) => set('spec_reference', e.target.value)} />
        </Field>
        <Field label="Symbol">
          <SymbolPicker value={d.symbol} color={d.color} onChange={(s) => set('symbol', s)} />
        </Field>
        <Field label="Colour">
          <ColorRow value={d.color} onChange={(c) => set('color', c)} />
        </Field>
        <Field label="Key (1–9)">
          <input className="rl-input" type="number" min={1} max={9} value={d.favorite_slot ?? ''} onChange={(e) => set('favorite_slot', e.target.value === '' ? null : Math.max(1, Math.min(9, Number(e.target.value))))} />
        </Field>
        <Field label="Notes" wide>
          <textarea className="rl-textarea" rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        <div className="rl-to-field" data-wide>
          <span>Package — {`qty per ${host}`}</span>
          {d.components.map((c, i) => (
            <div key={i} className="rl-to-row">
              <select
                className="rl-select"
                data-size="sm"
                value={c.item ? `i:${c.item}` : c.child_assembly ? `a:${c.child_assembly}` : ''}
                onChange={(e) => {
                  const [k, id] = e.target.value.split(':');
                  const comps = [...d.components];
                  comps[i] = { ...c, item: k === 'i' ? id : null, child_assembly: k === 'a' ? id : null };
                  set('components', comps);
                }}
                data-testid={`comp-${i}`}
              >
                <option value="">Pick an item…</option>
                {t.items.map((it) => (
                  <option key={it.id} value={`i:${it.id}`}>
                    {it.code} {it.description || it.name}
                  </option>
                ))}
                {t.assemblies
                  .filter((a) => a.id !== assembly?.id)
                  .map((a) => (
                    <option key={a.id} value={`a:${a.id}`}>
                      ▸ {a.name}
                    </option>
                  ))}
              </select>
              <input
                className="rl-input"
                data-size="sm"
                type="number"
                style={{ width: 64 }}
                value={c.qty_per_host}
                onChange={(e) => {
                  const comps = [...d.components];
                  comps[i] = { ...c, qty_per_host: Number(e.target.value) };
                  set('components', comps);
                }}
                aria-label={`qty per ${host}`}
                data-testid={`comp-qty-${i}`}
              />
              <button className="rl-icon-btn" aria-label="Remove" onClick={() => set('components', d.components.filter((_, k) => k !== i))}>
                <Icon name="close" size={12} />
              </button>
            </div>
          ))}
          <button className="rl-btn" data-size="sm" onClick={() => set('components', [...d.components, { item: null, child_assembly: null, qty_per_host: 1, qty_label: '' }])} data-testid="comp-add">
            <Icon name="plus" size={12} /> Add item
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

type Grouping = 'items' | 'hosts' | 'bom' | 'zones' | 'scopes' | 'sheets' | 'units';

/** AREA/ZONE rows summed per ZONE + TYPE (across areas and scopes). */
function byZone(rows: readonly { zone: string; type: string; item_id: string; qty: number }[]) {
  const m = new Map<string, { zone: string; type: string; item_id: string; qty: number }>();
  for (const r of rows) {
    const k = `${r.zone}|${r.item_id}`;
    const e = m.get(k) ?? { zone: r.zone, type: r.type, item_id: r.item_id, qty: 0 };
    e.qty += r.qty;
    m.set(k, e);
  }
  return [...m.values()].sort((a, b) => a.zone.localeCompare(b.zone) || a.type.localeCompare(b.type));
}

/** Rows per SCOPE (BASE BID first, then options / alternates) and TYPE. */
function byScope(rows: readonly { scope: string; type: string; item_id: string; qty: number }[]) {
  const m = new Map<string, { scope: string; type: string; item_id: string; qty: number }>();
  for (const r of rows) {
    const k = `${r.scope}|${r.item_id}`;
    const e = m.get(k) ?? { scope: r.scope, type: r.type, item_id: r.item_id, qty: 0 };
    e.qty += r.qty;
    m.set(k, e);
  }
  const rank = (s: string) => (s === 'BASE BID' ? 0 : 1);
  return [...m.values()].sort((a, b) => rank(a.scope) - rank(b.scope) || a.scope.localeCompare(b.scope) || a.type.localeCompare(b.type));
}

export function TotalsPanel({ t, onFocusTarget }: { t: TakeoffSession; onFocusTarget(target: TakeoffTarget): void }) {
  const [g, setG] = useState<Grouping>('items');
  const q = t.quantities;
  if (!q) return <div className="rl-center">{t.error ? <p className="rl-error">{t.error}</p> : <span className="rl-spinner" />}</div>;
  const focus = (target: TakeoffTarget) => {
    t.uiStore.set({ highlight: target.id });
    onFocusTarget(target);
  };
  const noScale = [...new Set(q.warnings.filter((w) => w.reason === 'no_scale').map((w) => w.page_index + 1))];
  return (
    <div className="rl-to-totals" data-testid="takeoff-totals">
      <div className="rl-to-row">
        <select className="rl-select" data-size="sm" value={g} onChange={(e) => setG(e.target.value as Grouping)} aria-label="Group totals" data-testid="totals-group">
          <option value="items">By item (BOM)</option>
          <option value="hosts">By assembly</option>
          <option value="bom">Area / zone rows</option>
          <option value="zones">By zone</option>
          <option value="scopes">By scope</option>
          <option value="sheets">By sheet</option>
          <option value="units">By typical unit</option>
        </select>
        <span className="rl-spacer" />
        {q.checks.length ? (
          <span className="rl-to-check" data-state="mismatch" title="Totals that don't match the expected figures">
            ⚠ {q.checks.length}
          </span>
        ) : null}
      </div>
      {noScale.length ? <p className="rl-to-warn">Set a scale on sheet {noScale.join(', ')} — runs there aren't counted yet.</p> : null}
      {q.warnings.some((w) => w.reason === 'unassigned') ? <p className="rl-to-warn">Some marks point at a deleted item.</p> : null}
      <table className="rl-to-table">
        {g === 'items' ? (
          <>
            <thead>
              <tr>
                <th>TYPE</th>
                <th>Description</th>
                <th className="num">Qty</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.items.map((i) => (
                <tr key={i.item_id} onClick={() => focus({ kind: 'item', id: i.item_id })} data-active={t.ui.highlight === i.item_id || undefined} data-testid={`total-${i.type}`}>
                  <td>
                    <b>{i.type}</b>
                  </td>
                  <td className="name">
                    {i.description || i.name}
                    {i.verify ? <span className="rl-to-flag">verify</span> : null}
                  </td>
                  <td className="num" data-testid={`total-qty-${i.type}`}>
                    {fmtQty(i.total)} <span className="rl-muted">{i.uom}</span>
                  </td>
                  <td>
                    <CheckBadge check={i.check} actual={i.total} expected={i.expected_total} />
                  </td>
                </tr>
              ))}
            </tbody>
          </>
        ) : g === 'hosts' ? (
          <>
            <thead>
              <tr>
                <th>Host</th>
                <th className="num">Count</th>
                <th>By area</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.hosts.map((h) => (
                <tr key={h.assembly_id} onClick={() => focus({ kind: 'assembly', id: h.assembly_id })} data-active={t.ui.highlight === h.assembly_id || undefined} data-testid={`host-total-${h.name}`}>
                  <td className="name">{h.name}</td>
                  <td className="num">{fmtQty(h.count)}</td>
                  <td className="rl-muted">{h.by_area.map((a) => `${a.area}: ${fmtQty(a.count)}`).join(' · ')}</td>
                  <td>
                    <CheckBadge check={h.check} actual={h.count} expected={h.expected_count} />
                  </td>
                </tr>
              ))}
            </tbody>
          </>
        ) : g === 'bom' ? (
          <>
            <thead>
              <tr>
                <th>Scope</th>
                <th>Area</th>
                <th>Zone</th>
                <th>QTY</th>
                <th>TYPE</th>
              </tr>
            </thead>
            <tbody>
              {q.rows.map((r, k) => (
                <tr key={k} onClick={() => focus({ kind: 'item', id: r.item_id })}>
                  <td>{r.scope}</td>
                  <td>{r.area}</td>
                  <td>{r.zone}</td>
                  <td title={`= ${fmtQty(r.qty)}`}>
                    <code>{r.formula}</code>
                  </td>
                  <td>
                    <b>{r.type}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </>
        ) : g === 'zones' ? (
          <>
            <thead>
              <tr>
                <th>Zone</th>
                <th>TYPE</th>
                <th className="num">Qty</th>
              </tr>
            </thead>
            <tbody data-testid="totals-by-zone">
              {byZone(q.rows).map((r, k) => (
                <tr key={k} onClick={() => focus({ kind: 'item', id: r.item_id })}>
                  <td>{r.zone || '\u2013'}</td>
                  <td>
                    <b>{r.type}</b>
                  </td>
                  <td className="num">{fmtQty(r.qty)}</td>
                </tr>
              ))}
            </tbody>
          </>
        ) : g === 'scopes' ? (
          <>
            <thead>
              <tr>
                <th>Scope</th>
                <th>TYPE</th>
                <th className="num">Qty</th>
              </tr>
            </thead>
            <tbody data-testid="totals-by-scope">
              {byScope(q.rows).map((r, k) => (
                <tr key={k} onClick={() => focus({ kind: 'item', id: r.item_id })} data-option={r.scope !== 'BASE BID' || undefined}>
                  <td>{r.scope}</td>
                  <td>
                    <b>{r.type}</b>
                  </td>
                  <td className="num">{fmtQty(r.qty)}</td>
                </tr>
              ))}
            </tbody>
          </>
        ) : g === 'sheets' ? (
          <>
            <thead>
              <tr>
                <th>Sheet</th>
                <th>TYPE</th>
                <th className="num">Qty</th>
              </tr>
            </thead>
            <tbody>
              {q.by_sheet.map((s, k) => (
                <tr key={k} onClick={() => focus({ kind: 'item', id: s.item_id })}>
                  <td>{s.sheet}</td>
                  <td>
                    <b>{s.type}</b>
                  </td>
                  <td className="num">{fmtQty(s.qty)}</td>
                </tr>
              ))}
            </tbody>
          </>
        ) : (
          <>
            <thead>
              <tr>
                <th>Unit</th>
                <th>TYPE</th>
                <th className="num">Per unit</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {q.by_unit.map((u, k) => (
                <tr key={k}>
                  <td>
                    {u.unit} <span className="rl-muted">×{fmtQty(u.multiplier)}</span>
                  </td>
                  <td>
                    <b>{u.type}</b>
                  </td>
                  <td className="num">{fmtQty(u.per_unit)}</td>
                  <td className="num">{fmtQty(u.qty)}</td>
                </tr>
              ))}
            </tbody>
          </>
        )}
      </table>
      {!q.items.length && !q.hosts.length ? <p className="rl-muted rl-to-empty">No quantities yet. Pick an item or assembly on the left and press Q to count.</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header: exports
// ---------------------------------------------------------------------------

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function TakeoffExportMenu({ t, title }: { t: TakeoffSession; title: string }) {
  const [busy, setBusy] = useState(false);
  const safe = (title || 'Takeoff').replace(/[^\w .-]+/g, '').trim() || 'Takeoff';
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <select
      className="rl-select"
      style={{ width: 'auto' }}
      value=""
      disabled={busy}
      aria-label="Export takeoff"
      data-testid="takeoff-export"
      onChange={(e) => {
        const v = e.target.value;
        e.target.value = '';
        if (v === 'xlsx') void run(async () => download(await t.client.exportXlsx(), `${safe} - Takeoff.xlsx`));
        else if (v.startsWith('csv:')) {
          const table = v.slice(4) as 'tuples' | 'project_bom' | 'area_zone_bom';
          void run(async () => download(await t.client.exportCsv(table), `${safe} - Takeoff ${table}.csv`));
        } else if (v === 'sheets')
          void run(async () => {
            const r = await t.client.exportSheets();
            toast(`Google Sheet created: ${r.name}`, 'info', 6000);
            window.open(r.url, '_blank', 'noopener');
          });
      }}
    >
      <option value="">{busy ? 'Exporting…' : 'Export'}</option>
      <option value="xlsx">Excel workbook (BOM tabs)</option>
      <option value="sheets">Google Sheets (new project sheet)</option>
      <option value="csv:project_bom">CSV — PROJECT BOM</option>
      <option value="csv:area_zone_bom">CSV — AREA/ZONE BOM</option>
      <option value="csv:tuples">CSV — raw tuples</option>
    </select>
  );
}

// ---------------------------------------------------------------------------
// Calibration
// ---------------------------------------------------------------------------

export interface CalibrationDraft {
  page: number;
  lengthPts: number;
}

export function CalibrateForm({ t, draft, onDone }: { t: TakeoffSession; draft: CalibrationDraft; onDone(): void }) {
  const [text, setText] = useState('');
  const [unit, setUnit] = useState<'ft' | 'm'>(t.sheetFor(draft.page)?.scale_unit ?? 'ft');
  const real = parseLength(text, unit);
  const save = async () => {
    if (!real || real <= 0) {
      toast("Enter the dimension's real length, e.g. 20'-0\" or 6.1 m", 'warning');
      return;
    }
    const ok = await t.setSheet(draft.page, { scale: { calibrate: { length_pts: draft.lengthPts, real_length: real, unit, label: `calibrated: ${text}` } } });
    if (ok) {
      toast(`Sheet scale set: ${fmtQty(draft.lengthPts / real)} pt per ${unit}`, 'info');
      onDone();
    }
  };
  return (
    <Modal
      title="Calibrate sheet scale"
      onClose={onDone}
      footer={
        <>
          <span className="rl-spacer" />
          <button className="rl-btn" onClick={onDone}>
            Cancel
          </button>
          <button className="rl-btn" data-variant="primary" onClick={() => void save()} data-testid="calibrate-save">
            Set scale
          </button>
        </>
      }
    >
      <p className="rl-muted">You drew {fmtQty(draft.lengthPts)} pt on the sheet. How long is it in reality?</p>
      <div className="rl-to-row">
        <input className="rl-input" autoFocus value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void save()} placeholder={unit === 'ft' ? `20'-0"` : '6.1'} data-testid="calibrate-length" />
        <select className="rl-select" data-size="sm" value={unit} onChange={(e) => setUnit(e.target.value as 'ft' | 'm')}>
          <option value="ft">feet</option>
          <option value="m">metres</option>
        </select>
      </div>
    </Modal>
  );
}
